import { Fragment, memo } from 'react'
import type { MouseEvent, ReactNode } from 'react'
import { TrashIcon } from '@heroicons/react/16/solid'
import type { WizardItemType } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { groupTarget } from '../../../Services/OrderWizard/issues.ts'
import type { ClickModifiers } from '../../../Services/OrderWizard/poolBoard.ts'
import { nameParts } from '../../../Services/OrderWizard/poolBoard.ts'
import { ItemTypeControl } from './ItemTypeControl.tsx'

export type Attention = 'error' | 'flagged'

export type ItemProps = {
  groupKey: string
  name: string
  /** The tooltip: the full name, and what else there is to know about the item. */
  title: string
  control: boolean
  /** A control recognised from its name rather than marked by the lab. */
  autoControl: boolean
  attention: Attention | undefined
  /** A short state shown next to the name. */
  tag?: string
  onSetType: (key: string, itemType: WizardItemType) => void
}

export type RowProps = ItemProps & {
  /** The name as shown: on a board of several pools, without the prefix every item shares. */
  shown: string
  /** Labels the type control "S" | "NC", for a narrow column. */
  compact: boolean
  selected: boolean
  dimmed: boolean
  onClickItem: (key: string, modifiers: ClickModifiers) => void
  onCheck: (key: string, shift: boolean) => void
}

/** The list's test ids start `wizard-pool-`, the board's `wizard-board-`. */
export type Layout = 'pool' | 'board'

const ItemName = ({ title, shown, layout }: { title: string; shown: string; layout: Layout }) => (
  <span className="line-clamp-2 min-w-0 flex-1 break-words" title={title} data-testid={`wizard-${layout}-item-name`}>
    {nameParts(shown).map((part, index) => (
      <Fragment key={index}>
        {index > 0 && <wbr />}
        {part}
      </Fragment>
    ))}
  </span>
)

const ITEM_CHECKBOX = 'checkbox checkbox-xs rounded-sm'

const ATTENTION_MARK: Record<Attention, string> = { error: 'border-l-red-400', flagged: 'border-l-amber-400' }

/** One item's row, alike in the list and on the board: its name and its type switch, between what each layout adds. */
const ItemRow = ({
  layout,
  item: { groupKey, name, title, control, autoControl, attention, tag, onSetType },
  shown,
  compact,
  className,
  leading,
  trailing,
  selected,
  onClick
}: {
  layout: Layout
  item: ItemProps
  shown: string
  compact: boolean
  className: string
  leading?: ReactNode
  trailing?: ReactNode
  selected?: boolean
  onClick?: (event: MouseEvent) => void
}) => (
  <li
    id={groupTarget(groupKey)}
    data-item={groupKey}
    data-testid={`wizard-${layout}-item`}
    data-selected={selected ? 'true' : undefined}
    data-attention={attention}
    className={`flex items-center gap-1.5 rounded border-l-2 px-1.5 py-0.5 transition-shadow ${
      attention ? ATTENTION_MARK[attention] : 'border-l-transparent'
    } ${className}`}
    onClick={onClick}
  >
    {leading}
    <ItemName title={title} shown={shown} layout={layout} />
    {tag && (
      <span className="shrink-0 rounded bg-amber-50 px-1 text-[0.7rem] text-amber-800" data-testid={`wizard-${layout}-item-tag`}>
        {tag}
      </span>
    )}
    <ItemTypeControl
      name={name}
      control={control}
      autoControl={autoControl}
      compact={compact}
      testId={`wizard-${layout}-item-type`}
      onSetType={(itemType) => onSetType(groupKey, itemType)}
    />
    {trailing}
  </li>
)

/** An item on the pool board: selected by click or checkbox, dragged with the selection. */
export const Row = memo(function Row({ shown, compact, selected, dimmed, onClickItem, onCheck, ...item }: RowProps) {
  const click = (event: MouseEvent) => {
    if ((event.target as Element).closest('input, button')) {
      return
    }
    onClickItem(item.groupKey, { shift: event.shiftKey, toggle: event.ctrlKey || event.metaKey })
  }
  return (
    <ItemRow
      layout="board"
      item={item}
      shown={shown}
      compact={compact}
      selected={selected}
      className={`group/row cursor-grab select-none ${selected ? 'bg-primary/15 ring-1 ring-inset ring-primary' : 'hover:bg-slate-100'} ${
        dimmed ? 'opacity-40' : ''
      }`}
      onClick={click}
      leading={
        <input
          type="checkbox"
          className={ITEM_CHECKBOX}
          checked={selected}
          aria-label={`Select ${item.name}`}
          onChange={(event) => onCheck(item.groupKey, (event.nativeEvent as Partial<globalThis.MouseEvent>).shiftKey === true)}
        />
      }
    />
  )
})

export type ListRowProps = ItemProps & {
  selected: boolean
  onCheck: (key: string, shift: boolean) => void
  onRemove: (key: string) => void
}

/** An item in the one-pool list: a checkbox for the selection bar, its type switch and a bin; a removal is undone with "Re-add" below. */
export const ListRow = memo(function ListRow({ selected, onCheck, onRemove, ...item }: ListRowProps) {
  const { groupKey, name } = item
  return (
    <ItemRow
      layout="pool"
      item={item}
      shown={name}
      compact={false}
      selected={selected}
      className={selected ? 'bg-primary/15' : 'hover:bg-slate-100'}
      leading={
        <input
          type="checkbox"
          className={ITEM_CHECKBOX}
          checked={selected}
          aria-label={`Select ${name}`}
          onChange={(event) => onCheck(groupKey, (event.nativeEvent as Partial<globalThis.MouseEvent>).shiftKey === true)}
        />
      }
      trailing={
        <button
          type="button"
          className="btn btn-ghost btn-xs h-5 min-h-0 shrink-0 px-1 text-slate-400 hover:text-red-600"
          aria-label={`Remove ${name}`}
          title={`Remove ${name}`}
          data-testid="wizard-pool-item-remove"
          onClick={() => onRemove(groupKey)}
        >
          <TrashIcon className="h-3.5 w-3.5" aria-hidden />
        </button>
      }
    />
  )
})
